import { DateTime } from 'luxon';

/**
 * Returns the hour as a zero-padded two-digit string: "00", "01", ... "22", "23".
 */
export function getHourString(dateTime: DateTime): string {
    const hour = dateTime.hour
    return hour < 10 ? '0' + hour.toString() : hour.toString();
}

/**
 * Returns the minute rounded to the nearest quarter (0, 15, 30, 45)
 * as a zero-padded two-digit string.
 */
export function getMinuteString(dateTime: DateTime): string {
    let minute = dateTime.minute
    // Round the minute to one of the following values: 0, 15, 30, 45
    if (minute % 15 !== 0) {
        minute = Math.round(minute / 15) * 15;
        if (minute === 60) {// make sure we dont get an over flow
            minute = 45;
        }
    }
    return minute < 10 ? '0' + minute.toString() : minute.toString();
}

/**
 * Validates that the provided string is a valid ISO 8601 date-time.
 * @throws {Error} If the date-time string is invalid.
 */
export function ensureValidDateString(dateString: string): void {
    const dateTime = DateTime.fromISO(dateString);
    if (!dateTime.isValid) {
        throw new Error(`Invalid date-time string: ${dateString}`);
    }
}

/**
 * Normalizes a date-time string to clean ISO format (no offset, no milliseconds).
 */
export function adjustDateString(dateString: string): string {
    const dateTime = DateTime.fromISO(dateString);
    return toCleanISO(dateTime);
}

/**
 * Returns true if startString <= endString (chronologically).
 */
export function isStartBeforeEndString(startString: string, endString: string): boolean {
    const startDateTime = DateTime.fromISO(startString);
    const endDateTime = DateTime.fromISO(endString);
    return startDateTime <= endDateTime;
}

/**
 * Converts a Luxon DateTime to a clean ISO string without offset and without milliseconds.
 * @returns e.g. "2025-04-20T17:00:00"
 */
export function toCleanISO(dateTime: DateTime): string {
    return dateTime.set({ millisecond: 0 }).toISO({ includeOffset: false, suppressMilliseconds: true })
}

/**
 * Adds one hour to a time string.
 * @param hourString - Format "HH:mm", e.g. "13:15"
 * @returns e.g. "14:15". Caps at "23:mm".
 */
export function addHour(hourString: string): string {
    if (hourString === '') {
        return '00:00';
    }
    let hours: string = hourString.substr(0, 2);
    const minutes = hourString.substr(3, 2);
    let hoursNumber = parseInt(hours, 10);
    if (hoursNumber < 23) {
        hoursNumber = hoursNumber + 1;
    }
    hours = hoursNumber.toString();
    if (hours.length < 2) {
        hours = '0' + hours;
    }
    return hours + ':' + minutes;
}

/**
 * @returns true if hour1 > hour2, false otherwise.
 */
export function isTimeStringBiggerThan(hour1: string, hour2: string): boolean {
    const hours1 = parseInt(hour1.substr(0, 2), 10);
    const hours2 = parseInt(hour2.substr(0, 2), 10);
    const minutes1 = parseInt(hour1.substr(3, 2), 10);
    const minutes2 = parseInt(hour2.substr(3, 2), 10);
    if (hours1 > hours2) {
        return true;
    }
    if (hours1 === hours2) {
        if (minutes1 > minutes2) {
            return true;
        }
    }
    return false;
}
